import { createRequire } from "node:module";
import { Command } from "commander";
import { parseOptions } from "./options.js";

const require = createRequire(import.meta.url);
const pkg = require("../../package.json");
import { computeFamiliarity } from "../core/familiarity.js";
import { renderTerminal } from "./output/terminal.js";
import { generateAndOpenHTML } from "./output/html.js";
import { computeTeamCoverage } from "../core/team-coverage.js";
import { renderCoverageTerminal } from "./output/coverage-terminal.js";
import { generateAndOpenCoverageHTML } from "./output/coverage-html.js";
import { computeMultiUser } from "../core/multi-user.js";
import { renderMultiUserTerminal } from "./output/multi-user-terminal.js";
import { generateAndOpenMultiUserHTML } from "./output/multi-user-html.js";
import { computeHotspots } from "../core/hotspot.js";
import { renderHotspotTerminal } from "./output/hotspot-terminal.js";
import { generateAndOpenHotspotHTML } from "./output/hotspot-html.js";
import { computeUnified } from "../core/unified.js";
import { generateAndOpenUnifiedHTML } from "./output/unified-html.js";
import {
  getDemoFamiliarityResult,
  getDemoHotspotResult,
  getDemoCoverageResult,
  getDemoMultiUserResult,
  getDemoUnifiedData,
} from "../core/demo.js";

function collect(value: string, previous: string[]): string[] {
  return previous.concat([value]);
}

export function createProgram(): Command {
  const program = new Command();

  program
    .name("gitfamiliar")
    .description("Visualize your code familiarity from Git history")
    .version(pkg.version)
    .option(
      "-m, --mode <mode>",
      "Scoring mode: committed, code-coverage, weighted",
      "committed",
    )
    .option(
      "-u, --user <user>",
      "Git user name or email (repeatable for comparison)",
      collect,
      [],
    )
    .option(
      "-e, --expiration <policy>",
      "Expiration policy: never, time:180d, change:50%, combined:365d:50%",
      "never",
    )
    .option("--html", "Generate HTML treemap report", false)
    .option(
      "-w, --weights <weights>",
      'Weights for weighted mode: blame,commit (e.g., "0.5,0.5")',
    )
    .option("--team", "Compare all contributors", false)
    .option(
      "--contributors-per-file",
      "Analyze number of contributors per file (bus factor)",
      false,
    )
    .option("--contributors", "Alias for --contributors-per-file")
    .option("--team-coverage", "Deprecated alias for --contributors-per-file")
    .option("--hotspot [mode]", "Hotspot analysis: personal (default) or team")
    .option("--since <days>", "Hotspot analysis period in days (default: 90)")
    .option("--window <days>", "Deprecated alias for --since")
    .option("--demo", "Show demo with sample data (no git repo needed)", false)
    .action(async (rawOptions) => {
      try {
        const repoPath = process.cwd();
        const options = parseOptions(rawOptions, repoPath);
        const isDemo = options.demo;
        const isMultiUser =
          options.team ||
          (Array.isArray(options.user) && options.user.length > 1);

        // Determine which feature to run and compute data
        if (
          options.html &&
          !options.hotspot &&
          !options.contributorsPerFile &&
          !isMultiUser
        ) {
          // Unified HTML dashboard
          const data = isDemo
            ? getDemoUnifiedData()
            : await computeUnified(options);
          await generateAndOpenUnifiedHTML(data, repoPath);
        } else if (options.hotspot) {
          const result = isDemo
            ? getDemoHotspotResult()
            : await computeHotspots(options);
          if (options.html) {
            await generateAndOpenHotspotHTML(result, repoPath);
          } else {
            renderHotspotTerminal(result);
          }
        } else if (options.contributorsPerFile) {
          const result = isDemo
            ? getDemoCoverageResult()
            : await computeTeamCoverage(options);
          if (options.html) {
            await generateAndOpenCoverageHTML(result, repoPath);
          } else {
            renderCoverageTerminal(result);
          }
        } else if (isMultiUser) {
          const result = isDemo
            ? getDemoMultiUserResult()
            : await computeMultiUser(options);
          if (options.html) {
            await generateAndOpenMultiUserHTML(result, repoPath);
          } else {
            renderMultiUserTerminal(result);
          }
        } else {
          const result = isDemo
            ? getDemoFamiliarityResult(options.mode)
            : await computeFamiliarity(options);
          if (options.html) {
            await generateAndOpenHTML(result, repoPath);
          } else {
            renderTerminal(result);
          }
        }
      } catch (error: any) {
        console.error(`Error: ${error.message}`);
        process.exit(1);
      }
    });

  return program;
}
